import type React from 'react';

import { Translate, TranslateHtml, useI18n } from '@change-corgi/core/react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { Chip } from '@change-corgi/design-system-legacy/components/content';
import { Textarea } from '@change-corgi/design-system-legacy/components/forms';
import { Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import type { UseFeedbackBodyModalResult } from '../../hooks/useFeedbackBodyModal';
import type { ModalOptions } from '../../hooks/useFeedbackModal';
import { REASONS } from '../../hooks/useFeedbackModal/constants';

type FeedbackFormOptions = {
	options: ModalOptions;
	modalData: UseFeedbackBodyModalResult;
};

export const FeedbackForm: React.FC<FeedbackFormOptions> = ({
	options: { recommendationGroup, recommendation },
	modalData: { actions, data },
}) => {
	const { translate } = useI18n();
	const hasUserFilledForm = !!data.extraFeedback || !!data.usefulness.length;
	return (
		<Flex sx={{ flexDirection: 'column', gap: 24, fontSize: '16px', lineHeight: '24px' }}>
			<Heading breakWord as="h1" size="h3" data-qa="sharing-recommendations-feedback-modal">
				<Translate value="corgi.petition.starter_dashboard.recommendations.feedback_modal.title" />
			</Heading>
			<Flex sx={{ flexDirection: 'column', gap: 12 }}>
				<Text fontWeight="semibold">
					<TranslateHtml
						value="corgi.petition.starter_dashboard.recommendations.feedback_modal.usefullness_text"
						replacements={{ petitionName: recommendation.name, recommendationGroup }}
					/>
				</Text>
				<Flex sx={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
					{REASONS.map((reason) => (
						<Chip
							key={reason.value}
							onClick={() => actions.handleSelectChip(reason.value)}
							selected={!!data.usefulness.includes(reason.value)}
						>
							{translate(`corgi.petition.starter_dashboard.recommendations.feedback_modal.${reason.id}`)}
						</Chip>
					))}
				</Flex>
			</Flex>
			<Flex sx={{ flexDirection: 'column' }}>
				<label id="extra-feedback-label" htmlFor="extra-feedback-input">
					<Text fontWeight="semibold">
						<Translate value="corgi.petition.starter_dashboard.recommendations.feedback_modal.extra_feedback.label" />
					</Text>

					<Textarea
						data-testid="extra-feedback"
						onChange={(event) => actions.setExtraFeedback(event.target.value)}
						value={data.extraFeedback}
						placeholder={translate(
							'corgi.petition.starter_dashboard.recommendations.feedback_modal.extra_feedback.placeholder',
						)}
						data-qa="share-recommendations-extra-feedback-input"
						rows={3}
						name="extra-feedback-input"
						aria-labelledby="extra-feedback-label"
						sx={{ mt: '12px' }}
					/>
				</label>
			</Flex>
			<Flex sx={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
				<Button
					data-testid="share-recommendations-feedback-submit"
					data-qa="share-recommendations-feedback-submit"
					variant="primary"
					type="button"
					sx={{ width: ['100%', 'auto'] }}
					onClick={() => actions.handleSubmitForm(recommendation)}
					disabled={!hasUserFilledForm}
				>
					<Translate value="corgi.petition.starter_dashboard.recommendations.feedback_modal.submitt_button" />
				</Button>
			</Flex>
		</Flex>
	);
};
